"use client";

import { useEffect, useState } from "react";
import type { ReportJob } from "@/lib/parcelq/types";
import {
  Pipeline_Elapsed_Label,
  Pipeline_Percent_Compute,
  Pipeline_Remaining_Label,
  Pipeline_Stage_Label,
  Pipeline_Status_Display,
  type Pipeline_Progress_Phase,
} from "@/lib/parcelq/pipeline-progress";
import {
  ParcelQ_Btn_Secondary_Class,
  ParcelQ_Heading_Class,
  ParcelQ_Label_Mono_Class,
} from "./parcelq-styles";

type Pipeline_State_Props = {
  Job: ReportJob | null;
  Avg_Duration_Ms: number;
  Phase: Pipeline_Progress_Phase;
  Pipeline_OnCancel?: () => void;
  Is_Cancelling?: boolean;
};

export function Pipeline_State({
  Job,
  Avg_Duration_Ms,
  Phase,
  Pipeline_OnCancel,
  Is_Cancelling = false,
}: Pipeline_State_Props) {
  const [Percent, Percent_Set] = useState(() => Pipeline_Percent_Compute(Job, Avg_Duration_Ms, Phase));
  const [Elapsed_Label, Elapsed_Label_Set] = useState(() => Pipeline_Elapsed_Label(Job, Phase));
  const [Remaining_Label, Remaining_Label_Set] = useState(() =>
    Pipeline_Remaining_Label(Job, Avg_Duration_Ms, Phase),
  );

  const Status_Label = Pipeline_Status_Display(Job, Phase);
  const Stage_Label = Pipeline_Stage_Label(Job);

  useEffect(() => {
    function Tick() {
      Percent_Set(Pipeline_Percent_Compute(Job, Avg_Duration_Ms, Phase));
      Elapsed_Label_Set(Pipeline_Elapsed_Label(Job, Phase));
      Remaining_Label_Set(Pipeline_Remaining_Label(Job, Avg_Duration_Ms, Phase));
    }

    Tick();
    const Timer = window.setInterval(Tick, 1000);
    return () => window.clearInterval(Timer);
  }, [Avg_Duration_Ms, Job, Phase]);

  return (
    <section className="flex flex-1 flex-col items-center justify-center px-6 py-24">
      <div className="w-full max-w-lg text-center">
        <p className={ParcelQ_Label_Mono_Class}>Running pipeline</p>
        <h1 className={`mt-3 text-2xl ${ParcelQ_Heading_Class}`}>Generating reports</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">
          May take up to 5 minutes. Keep this tab open.
        </p>

        <div className="mx-auto mt-8 w-full max-w-sm">
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <span className="font-mono text-2xl font-semibold tabular-nums text-amber-800">{Percent}%</span>
            <span className={`${ParcelQ_Label_Mono_Class} text-pq-fg-subtle`}>{Elapsed_Label}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-pq-bg-elevated/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-600 to-amber-400 transition-[width] duration-500 ease-out"
              style={{ width: `${Percent}%` }}
            />
          </div>
          {Remaining_Label ? (
            <p className={`mt-3 ${ParcelQ_Label_Mono_Class} text-pq-fg-subtle`}>{Remaining_Label}</p>
          ) : null}
        </div>

        {Job ? (
          <dl className="mx-auto mt-8 max-w-sm rounded-2xl border border-pq-border bg-pq-panel p-5 text-left text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-pq-fg-subtle">Status</dt>
              <dd className="text-right text-amber-800">{Status_Label}</dd>
            </div>
            {Stage_Label ? (
              <div className="mt-3 flex justify-between gap-4">
                <dt className="text-pq-fg-subtle">Stage</dt>
                <dd className="font-mono text-slate-200">{Stage_Label}</dd>
              </div>
            ) : null}
            <div className="mt-3 flex justify-between gap-4">
              <dt className="text-pq-fg-subtle">Bundle</dt>
              <dd className="capitalize text-slate-200">{Job.bundle}</dd>
            </div>
            {Job.radius_miles != null ? (
              <div className="mt-3 flex justify-between gap-4">
                <dt className="text-pq-fg-subtle">Radius</dt>
                <dd className="text-slate-200">{Job.radius_miles} mi</dd>
              </div>
            ) : null}
            {Job.tmux_session ? (
              <div className="mt-4 border-t border-pq-border pt-4">
                <dt className="text-pq-fg-subtle">Live log (SSH)</dt>
                <dd className="mt-2 font-mono text-xs leading-relaxed text-amber-800">
                  tmux attach -t {Job.tmux_session}
                </dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {Pipeline_OnCancel ? (
          <button
            type="button"
            onClick={Pipeline_OnCancel}
            disabled={Is_Cancelling}
            className={`mt-8 ${ParcelQ_Btn_Secondary_Class} disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {Is_Cancelling ? "Cancelling…" : "Cancel"}
          </button>
        ) : null}
      </div>
    </section>
  );
}
